import React, { useEffect, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import {
  DataResult,
  State,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import { useNavigate } from 'react-router-dom';
import {
  DeleteEventAsync,
  GetEventById,
  GetEventFeedbacks,
  GetEventPlaylistFacilitators,
  GetEventPlaylistLocations,
  GetEventPlaylists,
  GetEvents,
  PostEventAsync,
  PostEventFeedbackAsync,
  PostEventPlaylistAsync,
  PostEventPlaylistFacilitatorAsync,
  PostEventPlaylistLocationAsync
} from '../services/events';
import { ColumnMenu } from '../components/columnMenu';
import { Button } from '@progress/kendo-react-buttons';
import Toolbar from '../components/page-toolbar';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import EventsScheduler from './events-scheduler';
import { ActionCell, ActionsCell } from '../components/grid/actions-cell';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import { YesNoDialogResult } from '../types/dialog-result';
import { appStore } from '../stores/app-store';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { useToast } from '../contexts/toast-context';
import { ArchiveTiloGameAsync } from '../services/tilo/tilo-proxy';
import { useGetEvents } from '../hooks/services/use-events';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'startDateUtc', dir: 'desc' }],
  filter: DefaultGridSettings.initialDataState.filter,
  skip: DefaultGridSettings.initialDataState.skip,
  group: DefaultGridSettings.initialDataState.group
};

const ADJUST_PADDING: number = 4;
const COLUMN_MIN: number = 4;

const EventsGrid = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>({
    ...initialDataState,
    take: DefaultGridSettings.initialDataState.take
  });
  const [gridMode, setGridMode] = useState<string>('grid');
  const [selectedId, setSelectedId] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [applyMinWidth, setApplyMinWidth] = React.useState(false);
  const [gridCurrent, setGridCurrent] = React.useState(0);

  const { onToastOpen } = useToast();

  const minGridWidth = React.useRef<number>(0);
  const grid = React.useRef<any>(null);

  const { data, isLoading, mutate } = useGetEvents(
    toDataSourceRequestString(dataState)
  );

  const addDaysToDate = (date: Date, daysToAdd: number) => {
    const addDays = (date: Date, days: number): Date => {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    };

    const modifiedDate = addDays(date, daysToAdd);

    return modifiedDate;
  };

  const copyEventById = async (id: number) => {
    try {
      appStore.showLoading();

      const [event, dataEventPlaylistById, dataEventFeedbackById] =
        await Promise.all([
          GetEventById(id),
          GetEventPlaylists(id),
          GetEventFeedbacks(id)
        ]);

      const newEvent = {
        ...event,
        name: `${event.name}-copy`,
        id: 0,
        status: 'New'
      };

      const copiedEvent = await PostEventAsync(newEvent);

      if (dataEventPlaylistById.total > 0) {
        const playlistPromises = dataEventPlaylistById.data
          .filter((item) => item.isDeleted === false)
          .map(async (playlist) => {
            const nowPlusWeek = new Date(
              new Date().setDate(new Date().getDate() + 7)
            );
            const newPlaylist = {
              ...playlist,
              eventId: copiedEvent.id,
              startDateUtc: nowPlusWeek,
              endDateUtc: nowPlusWeek
            };

            const responsePostPlaylist = await PostEventPlaylistAsync(
              copiedEvent.id!,
              newPlaylist
            );

            const [facilitators, locations] = await Promise.all([
              GetEventPlaylistFacilitators(id, playlist.id!),
              GetEventPlaylistLocations(id, playlist.id!)
            ]);

            await Promise.all([
              ...facilitators.data.map((f) =>
                PostEventPlaylistFacilitatorAsync(
                  copiedEvent.id!,
                  responsePostPlaylist.id!,
                  { ...f, id: 0 }
                )
              ),
              ...locations.data.map((l) =>
                PostEventPlaylistLocationAsync(
                  copiedEvent.id!,
                  responsePostPlaylist.id!,
                  { ...l, id: 0 }
                )
              )
            ]);
          });

        await Promise.all(playlistPromises);
      }

      if (dataEventFeedbackById.total > 0) {
        const feedbackPromises = dataEventFeedbackById.data
          .filter((item) => item.isDeleted === false)
          .map((feedbackItem) =>
            PostEventFeedbackAsync(copiedEvent.id!, feedbackItem)
          );

        await Promise.all(feedbackPromises);
      }

      // navigate after all operations are complete
      navigate(`/myevents/edit/${copiedEvent.id}`);
    } catch (error) {
      console.error('Error copying event:', error);
      onToastOpen({
        style: 'error',
        title: 'My events',
        body: <div>Failed to copy event.</div>
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onAddEvent = () => {
    navigate('/myevents/new');
  };

  const onCopyEvent = async (id: number) => {
    copyEventById(id);
  };

  const onDelete = (id: number) => {
    onDeleteEvent(id);
  };

  const onDeleteEvent = async (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirm(true);
  };

  const gameColumn = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span>{`${determineEventType(props)}`}</span>
      </td>
    );
  };

  const determineEventType = (props: GridCellProps) => {
    const eventTypes: string[] = [];

    if (props.dataItem.gameId !== null) {
      eventTypes.push('Game');
    }

    if (props.dataItem.assessmentId !== null) {
      eventTypes.push('Assessment');
    }

    if (props.dataItem.feedbackId !== null) {
      eventTypes.push('Feedback');
    }

    return eventTypes.length === 0 ? 'No ' : eventTypes.join(', ');
  };

  const onActionRow = (props: GridCellProps) => {
    return (
      <>
        <ActionsCell {...props}>
          <ActionCell
            title={'View event'}
            icon={'visibility'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => onViewEvent(props.dataItem.id)}
          />
          {props.dataItem.status !== 'Completed' && (
            <ActionCell
              title={'Edit event'}
              icon={'edit'}
              themeColor={'primary'}
              className={'fs-2'}
              onClick={() => onEditEvent(props.dataItem.id)}
            />
          )}
          <ActionCell
            title={'Copy event'}
            icon={'content_copy'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => onCopyEvent(props.dataItem.id)}
          />
          <ActionCell
            title={'Delete event'}
            icon={'delete'}
            themeColor={'error'}
            className={'fs-2'}
            onClick={() => onDelete(props.dataItem.id)}
          />
        </ActionsCell>
      </>
    );
  };

  const onColumnTextClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer'}
          onClick={() => handleNameClick(props.dataItem.id, 'grid')}>
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const handleNameClick = (id: number, viewSource?: string) => {
    navigate('/myeventview/' + id);
  };

  const onEditEvent = (id: number) => {
    navigate('/myevents/edit/' + id);
  };

  const onViewEvent = (id: number) => {
    navigate('/myeventview/' + id);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        appStore.showLoading();

        // Archive any TILO games linked to this event before deleting
        const playlists = await GetEventPlaylists(selectedId);
        for (const pl of playlists.data ?? []) {
          if (pl.type === 'TILO' && pl.tiloGameId) {
            await ArchiveTiloGameAsync(pl.tiloGameId);
          }
        }

        await DeleteEventAsync(selectedId);
        mutate();
        onToastOpen({
          style: 'success',
          title: 'My events',
          body: <div>Event deleted.</div>
        });
      } catch {
        onToastOpen({
          style: 'error',
          title: 'My events',
          body: <div>Failed to delete event.</div>
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setShowDeleteConfirm(false);
  };

  const closeDeleteDialog = () => {
    setShowDeleteConfirm(false);
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.startDateUtc, true)}
      </td>
    );
  };

  const StatusCell = (props: GridCellProps) => {
    if (
      props.dataItem.tilIsSynced !== undefined &&
      props.dataItem.tilIsSynced !== null
    ) {
      return (
        <td className={'k-table-td'}>
          {props.dataItem.tilIsSynced ? 'Synced' : 'Unsynced'}
        </td>
      );
    }
    return <td className={'k-table-td'}>{props.dataItem.status}</td>;
  };

  useEffect(() => {
    grid.current = document.querySelector('.k-grid');
    window.addEventListener('resize', handleResize);

    setGridCurrent(grid.current.offsetWidth);
    setApplyMinWidth(grid.current.offsetWidth < minGridWidth.current);
  }, []);

  const handleResize = () => {
    if (grid.current.offsetWidth < minGridWidth.current && !applyMinWidth) {
      setApplyMinWidth(true);
    } else if (grid.current.offsetWidth > minGridWidth.current) {
      setGridCurrent(grid.current.offsetWidth);
      setApplyMinWidth(false);
    }
  };

  const setWidth = (minWidth: number | undefined) => {
    if (minWidth === undefined) {
      minWidth = 0;
    }
    let width = applyMinWidth
      ? minWidth
      : minWidth + (gridCurrent - minGridWidth.current) / 8;
    if (width >= COLUMN_MIN) {
      width -= ADJUST_PADDING;
    }
    return width;
  };

  return (
    <>
      <Toolbar title={gridMode === 'grid' ? 'My events' : 'Schedule'}>
        <HelpSupport
          title="My events guide"
          url="https://forum.catalystglobal.com/t/4140"
        />
        <Button onClick={onAddEvent} themeColor={'primary'}>
          Add new
        </Button>
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search events'}
          columnsToSearch={[
            'name',
            'game',
            'clientName',
            'leadFacilitatorName'
          ]}
          showCalendarMode={true}
          showCardMode={false}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            const newState = {
              ...initialDataState
            };
            if (e.gridMode === 'grid') {
              newState.take = DefaultGridSettings.initialDataState.take;
            }
            setDataState(newState);
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`events.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-events'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'startDateUtc'}
                width={setWidth(70)}
                title={'Date'}
                cells={{ data: DateCell }}
              />
              <Column
                field={'name'}
                title={'Name'}
                cells={{ data: onColumnTextClick }}
                columnMenu={ColumnMenu}
                width={setWidth(50)}
              />
              <Column
                cells={{ data: gameColumn }}
                title={'Type'}
                width={setWidth(30)}
              />
              <Column
                field={'clientName'}
                title={'Client/Agent'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'leadFacilitatorName'}
                title={'Facilitator'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'approxPax'}
                title={'Pax'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'status'}
                title={'Status'}
                columnMenu={ColumnMenu}
                cells={{ data: StatusCell }}
              />
              <Column
                field={'eventActions'}
                title={'Actions'}
                cells={{ data: onActionRow }}
                width={210}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
          {gridMode === 'cal' && (
            <EventsScheduler eventData={data as DataResult} />
          )}
        </ExcelExport>
        {showDeleteConfirm && (
          <YesNoDialog
            title={'Remove Event'}
            onConfirm={onConfirmDelete}
            onClose={closeDeleteDialog}>
            Are you sure you want to delete this event?
          </YesNoDialog>
        )}
      </div>
    </>
  );
};
export default EventsGrid;
